import {
  getCountryMappings,
  getStorePageLinks,
  StorePageLinksProps,
  AllCountriesDataProps
} from '@uma/react-commons/dist/index';
import {
  IconLockAccount,
  IconPrinterInkDrop,
  IconRearrange,
  IconShoppingCart,
  IconTruck
} from '@veneer/core';
import React from 'react';
import { TranslateType } from 'src/contexts/dependencyManager/types';
import {
  getCardsInstantInkRequestMoreInk,
  getProliveSupportUrl,
  URLS
} from 'src/utils/urls';
import {
  AddPrinterHyperlinkClicked,
  ManageHpAllInPlanHyperlinkClicked,
  ManageInstantInkPlanHyperlinkClicked,
  ChangePasswordHyperlinkClicked,
  ChangePasswordOnPortalHyperlinkClicked,
  ContactHpAllInProLiveSupportHyperlinkClicked,
  GetProductSupportHyperlinkClicked,
  PrinterShowsAccountIssueHyperlinkClicked,
  RegisterDeviceHyperlinkClicked,
  RequestMoreInkHyperlinkClicked,
  StartReturnHyperlinkClicked,
  UpdateHpAllInPaymentMethodHyperlinkClicked,
  UpdateInstantInkPaymentMethodHyperlinkClicked,
  UpdatePersonalDetailsHyperlinkClicked,
  ViewHpAllInPrintUsageHyperlinkClicked,
  ViewInstantInkPrintUsageHyperlinkClicked,
  ViewOrderHistoryHyperlinkClicked
} from './analytics';
import { IFrameCardProps } from './FrameCardTemplate';

export const getLoginAndSecurityCard = (
  translate: TranslateType,
  flagChangePassword: boolean,
  isLoading: boolean
): IFrameCardProps => ({
  title: translate('home.cards.loginAndSecurity.title', 'Login & security'),
  urls: [
    {
      link:
        flagChangePassword && !isLoading
          ? URLS.Cards.LoginAndSecurity.ChangePasswordOnPortal
          : URLS.Cards.LoginAndSecurity.ChangePassword,
      text: translate(
        'home.cards.loginAndSecurity.changePassword',
        'Change password'
      ),
      analyticsEvent:
        flagChangePassword && !isLoading
          ? ChangePasswordOnPortalHyperlinkClicked
          : ChangePasswordHyperlinkClicked,
      isInternalLink: flagChangePassword && !isLoading
    },
    {
      link: URLS.Cards.LoginAndSecurity.UpdatePersonalDetailsOnPortal,
      text: translate(
        'home.cards.loginAndSecurity.updatePersonalDetails',
        'Update personal details'
      ),
      analyticsEvent: UpdatePersonalDetailsHyperlinkClicked,
      isInternalLink: true
    }
  ],
  icon: <IconLockAccount size={30} />
});

export const getYourPurchasesCard = async (
  translate: TranslateType,
  languageCode: string,
  flagHomeCountryBasedExperience: boolean,
  isLoadingFeatureFlags: boolean
): Promise<IFrameCardProps | null> => {
  const hasYourPurchases = await getCountryMappings(
    AllCountriesDataProps.HasYourPurchases
  );

  if (isLoadingFeatureFlags) {
    return { loading: true };
  }

  const showDynamicInfos = hasYourPurchases && flagHomeCountryBasedExperience;

  if (!hasYourPurchases && flagHomeCountryBasedExperience) {
    return null;
  }

  const [orderHistoryLink, startReturnLink] = showDynamicInfos
    ? await Promise.all([
        getStorePageLinks(
          languageCode,
          StorePageLinksProps.OrderHistory,
          URLS.Cards.YourPurchases.ViewOrderHistory
        ),
        getStorePageLinks(
          languageCode,
          StorePageLinksProps.StartAReturn,
          URLS.Cards.YourPurchases.StartReturn
        )
      ])
    : [
        URLS.Cards.YourPurchases.ViewOrderHistory,
        URLS.Cards.YourPurchases.StartReturn
      ];

  return {
    title: translate('home.cards.yourPurchases.title', 'Your purchases'),
    urls: [
      {
        link: orderHistoryLink,
        text: translate(
          'home.cards.yourPurchases.viewOrderHistory',
          'View order history'
        ),
        analyticsEvent: ViewOrderHistoryHyperlinkClicked(orderHistoryLink)
      },
      {
        link: startReturnLink,
        text: translate(
          'home.cards.yourPurchases.startReturn',
          'Start a return'
        ),
        analyticsEvent: StartReturnHyperlinkClicked(startReturnLink)
      }
    ],
    icon: <IconShoppingCart size={30} />
  };
};

export const getOtherLinksCard = (
  translate: TranslateType
): IFrameCardProps => ({
  title: translate('home.cards.otherLinks.title', 'Other links'),
  urls: [
    {
      link: URLS.Cards.OtherLinks.PrinterShowsAccountIssue,
      text: translate(
        'home.cards.otherLinks.printerShowsAccountIssue',
        'Printer shows account issue'
      ),
      analyticsEvent: PrinterShowsAccountIssueHyperlinkClicked
    },
    {
      link: URLS.Cards.OtherLinks.AddPrinter,
      text: translate(
        'home.cards.otherLinks.addPrinterToAccount',
        'Add a printer to your account'
      ),
      analyticsEvent: AddPrinterHyperlinkClicked
    },
    {
      link: URLS.Cards.OtherLinks.GetProductSupport,
      text: translate(
        'home.cards.otherLinks.getProductSupport',
        'Get product support'
      ),
      analyticsEvent: GetProductSupportHyperlinkClicked
    },
    {
      link: URLS.Cards.OtherLinks.RegisterDevice,
      text: translate(
        'home.cards.otherLinks.registerDevice',
        'Register a device'
      ),
      analyticsEvent: RegisterDeviceHyperlinkClicked
    }
  ],
  icon: <IconRearrange size={30} />
});

export const getInstantInkCard = async (
  translate: TranslateType,
  language: string,
  country: string
): Promise<IFrameCardProps> => {
  const requestMoreInkLink = await getCardsInstantInkRequestMoreInk(
    language,
    country
  );

  return {
    title: translate('common.instantInk', 'HP Instant Ink'),
    urls: [
      {
        link: URLS.Cards.InstantInk.UpdatePaymentMethod,
        text: translate(
          'home.cards.updatePaymentMethod',
          'Update payment method'
        ),
        analyticsEvent: UpdateInstantInkPaymentMethodHyperlinkClicked
      },
      {
        link: URLS.Cards.InstantInk.ManagePlan,
        text: translate('home.cards.managePlan', 'Manage plan'),
        analyticsEvent: ManageInstantInkPlanHyperlinkClicked
      },
      {
        link: URLS.Cards.InstantInk.ViewPrintUsage,
        text: translate('home.cards.viewPrintUsage', 'View print usage'),
        analyticsEvent: ViewInstantInkPrintUsageHyperlinkClicked
      },
      {
        link: requestMoreInkLink,
        text: translate(
          'home.cards.hpInstantInk.requestMoreInk',
          'Request more ink'
        ),
        analyticsEvent: RequestMoreInkHyperlinkClicked(requestMoreInkLink)
      }
    ],
    icon: <IconTruck size={30} />
  };
};

export const getHpAllInCard = (
  translate: TranslateType,
  country: string,
  language: string
): IFrameCardProps => ({
  title: translate('common.allIn', 'HP All-In Plan'),
  urls: [
    {
      link: URLS.Cards.HpAllIn.UpdatePaymentMethod,
      text: translate(
        'home.cards.updatePaymentMethod',
        'Update payment method'
      ),
      analyticsEvent: UpdateHpAllInPaymentMethodHyperlinkClicked
    },
    {
      link: URLS.Cards.HpAllIn.ManagePlan,
      text: translate('home.cards.managePlan', 'Manage plan'),
      analyticsEvent: ManageHpAllInPlanHyperlinkClicked
    },
    {
      link: URLS.Cards.HpAllIn.ViewPrintUsage,
      text: translate('home.cards.viewPrinterUsage', 'View print usage'),
      analyticsEvent: ViewHpAllInPrintUsageHyperlinkClicked
    },
    {
      link: getProliveSupportUrl(language, country),
      text: translate(
        'home.cards.hpAllIn.contactProLiveSupport',
        'Contact 24/7 Pro live support'
      ),
      analyticsEvent: ContactHpAllInProLiveSupportHyperlinkClicked(
        country,
        language
      )
    }
  ],
  icon: <IconPrinterInkDrop size={30} />
});
